import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api.js";

/**
 * The Download tab's frame: the quality, the videos, one export button and
 * what goes with it, then the zip and the earlier exports. The page builds
 * each part, because the page owns the export; this only lays them out.
 */
export const DownloadTab: React.FC<{
  projectId: string;
  quality: React.ReactNode;
  videos: React.ReactNode;
  /** The export button, and Stop while languages render. */
  actions: React.ReactNode;
  /** Under the button: where it renders, or why this browser cannot. */
  note: string | null;
  /** Whether any language has a file the zip would hold. */
  zippable: boolean;
  /** Why no language may be added, when none may; else the link to add one. */
  blocked: string | null;
  history: React.ReactNode;
}> = ({ projectId, quality, videos, actions, note, zippable, blocked, history }) => {
  const [zipping, setZipping] = useState(false);
  const [zipError, setZipError] = useState<string | null>(null);

  const zip = async () => {
    setZipping(true);
    setZipError(null);
    try {
      const { url } = await api.languagesDownloadLink(projectId);
      window.location.assign(url);
    } catch {
      setZipError("The download couldn't be prepared. Try again in a moment.");
    } finally {
      setZipping(false);
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <section aria-labelledby="export-quality" className="flex flex-col gap-2">
        <h2 id="export-quality" className="text-[13px] font-semibold text-ink-200">
          Quality
        </h2>
        {quality}
      </section>

      <section aria-labelledby="export-videos" className="flex flex-col gap-2">
        <div className="flex items-baseline gap-2">
          <h2 id="export-videos" className="flex-1 text-[13px] font-semibold text-ink-200">
            Videos
          </h2>
          {blocked ? null : (
            <Link
              to={`/projects/${projectId}/editor?languages=open`}
              className="text-[13px] font-semibold text-ember-600 hover:underline"
            >
              + Add a language
            </Link>
          )}
        </div>
        {blocked ? <p className="text-[12.5px] text-ink-300">{blocked}</p> : null}
        {videos}
      </section>

      <div className="flex flex-col gap-2">
        <div className="flex gap-2 [&>.btn-primary]:flex-1">{actions}</div>
        {note ? <p className="text-center text-[12px] text-ink-300">{note}</p> : null}
        {zippable ? (
          <button type="button" className="btn btn-secondary" disabled={zipping} onClick={() => void zip()}>
            {zipping ? "Preparing…" : "Download everything · .zip"}
          </button>
        ) : null}
        {zipError ? <p className="text-center text-[12px] text-signal-bad">{zipError}</p> : null}
      </div>

      {history}
    </div>
  );
};
